import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibAddIcon,
  KibAttachIcon,
  KibDeleteFillIcon,
  KibVisibilityOnIcon,
} from '@chewy/kib-icons-react'
import * as R from 'ramda'
import { AlertIconType, LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import type { LabTestLogFile } from '~/api/graphql/generated/types'
import MultiFileSelector from '~/components/common/inputs/MultiFileSelector'
import type { LabTestLogFileMapped } from '~/components/dashboard/lab-tests-dashboard/lab-tests-table/LabTestAttachmentList'
import DialogNames from '~/constants/DialogNames'
import DocumentDialogStates from '~/constants/DocumentDialogStates'
import { matchImageExtension } from '~/constants/extensions'
import {
  createLabTestAttachmentFileGroup,
  deleteLabTestAttachmentFile,
  getLabTestLogFileGroups,
} from '~/store/duck/labTestsDashboard'
import { getDocumentTypes, getLabVendors } from '~/store/reducers/constants'
import {
  getAppointmentId,
  getClientId,
  getPatientId,
} from '~/store/reducers/soap'
import type { Document, FilesWithDetails, LabTestDashboardTest } from '~/types'
import useDialog from '~/utils/useDialog'

import LabNotesEditor, { type LabNotesEditorHandle } from './LabNotesEditor'
import LabStatusSelect from './LabStatusSelect'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './LabTestItem.module.scss'

interface LabTestItemProps {
  disabled?: boolean
  labTestIdentifier: string
  labTestItem: LabTestDashboardTest
  notes: string
  onNotesChange: (notes: string) => void
  onStatusChange: (statusId: string) => void
  selectedStatusId: string
  testId: string
  vendorId: string
}

const LabTestItem = ({
  labTestItem,
  vendorId,
  notes,
  selectedStatusId,
  onNotesChange,
  onStatusChange,
  disabled = false,
  testId,
  labTestIdentifier,
}: LabTestItemProps) => {
  const { t } = useTranslation(['Common', 'LabTests'])
  const dispatch = useDispatch()
  const files = (useSelector(
    getLabTestLogFileGroups(labTestIdentifier, testId),
  ) || []) as LabTestLogFileMapped[]
  const documentTypes = useSelector(getDocumentTypes)
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const eventId = useSelector(getAppointmentId)

  const [openDocumentDialog] = useDialog(DialogNames.DOCUMENT)
  const [openSelectAttachmentDialog] = useDialog(DialogNames.SELECT_ATTACHMENT)
  const [openFileAlert] = useDialog(DialogNames.DISMISSIBLE_ALERT)

  const templateType = Utils.findConstantByName('Notes template', documentTypes)
  const LabVendors = useSelector(getLabVendors)
  const vendorName = LanguageUtils.getConstantTranslatedName(
    vendorId || '',
    LabVendors,
  )

  const editorRef = React.useRef<LabNotesEditorHandle>(null)

  const onDocumentsFetched = (docs: Document[]) => {
    if (docs.length > 0) {
      const [embeddableDocs, fileDocs] = R.partition(
        (document: Document) => Boolean(document.template?.body),
        docs,
      ) as [Document[], Document[]]

      const htmlBodyDocuments = embeddableDocs.map(
        (document) => document.template?.body,
      )
      const imageDocuments = fileDocs
        .filter(
          ({ template }) =>
            matchImageExtension(template?.extension) && template?.fileUrl,
        )
        .map(
          ({ template }) =>
            `<img src="${window.location.origin}${template?.fileUrl}" alt="${template?.fileName}" />`,
        )
      const bodies = [...htmlBodyDocuments, ...imageDocuments]
      if (!bodies.length) {
        return
      }

      const htmlContent = bodies.join('\n')
      editorRef.current?.appendHtml(htmlContent)
    }
  }

  const handleAddNotesTemplate = () => {
    openSelectAttachmentDialog({
      documentTypes: [templateType],
      eventId,
      clientId,
      enrich: true,
      patientId,
      label: t('Common:NOTES_TEMPLATE').toLowerCase(),
      onDocumentsFetched,
      convertToTextDocument: false,
      skipFileTemplates: false,
    })
  }

  const handleAddAttachment = (selectedFiles: File[]) => {
    // Check file limit (max 10 files as per LabTestAddAttachment pattern)
    const fileCount = (files?.length ?? 0) + selectedFiles.length
    if (fileCount > 10) {
      openFileAlert({
        iconType: AlertIconType.WARN,
        message: <>{t('Common:INPUTS.FILE_SELECTOR.ALERT_MESSAGE_COUNT')}</>,
      })
    } else {
      // Use labTestsDashboard action to create file attachments
      const attachmentGroup: FilesWithDetails = {
        title: labTestItem.name || '',
        description: vendorName || '',
        date: new Date().toISOString(),
        files: selectedFiles,
      }

      dispatch(
        createLabTestAttachmentFileGroup({
          labTestLogId: labTestItem.id,
          attachmentGroup,
          clientId: clientId ?? '',
          patientId: patientId ?? '',
          labTestIdentifier,
        }),
      )
    }
  }

  const handlePreview = (file: LabTestLogFile) => {
    openDocumentDialog({
      previewOnly: true,
      document: file,
      PreviewProps: {
        hideTopButtons: true,
        view: true,
        isSOAP: true,
        showChangeFile: false,
      },
      step: DocumentDialogStates.PREVIEW,
    })
  }

  const handleStatusChange = (newStatusId: string) => {
    onStatusChange(newStatusId)
  }

  const handleDeleteFile = (file: LabTestLogFileMapped) => {
    dispatch(
      deleteLabTestAttachmentFile({
        labTestIdentifier,
        labTestLogId: labTestItem.id,
        groupId: file.groupId,
        fileId: file.id,
      }),
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h4 className={styles.testTitle}>{labTestItem.name || ''}</h4>
        <LabStatusSelect
          value={selectedStatusId}
          onChange={handleStatusChange}
        />
      </div>

      <div className={styles.middle}>
        <div className={styles.textarea}>
          <LabNotesEditor
            disabled={disabled}
            ref={editorRef}
            value={notes}
            onChange={onNotesChange}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <KibButtonNew
          disabled={disabled}
          emphasis="tertiary"
          icon={<KibAddIcon />}
          size="small"
          theme="utility"
          onClick={handleAddNotesTemplate}
        >
          {t('Common:ADD_NOTES_TEMPLATE')}
        </KibButtonNew>
        <MultiFileSelector onSelected={handleAddAttachment}>
          <KibButtonNew
            disabled={disabled}
            emphasis="tertiary"
            icon={<KibAttachIcon />}
            size="small"
            theme="utility"
          >
            {t('Common:ADD_ATTACHMENT')}
          </KibButtonNew>
        </MultiFileSelector>
      </div>

      {/* Attachments Section */}
      {files && files.length > 0 && (
        <div className={styles.attachments}>
          <h5 className={styles.attachmentsTitle}>
            {t('Common:ATTACHMENTS')} ({files.length})
          </h5>
          <div className={styles.attachmentsList}>
            {files.map((file) => (
              <div className={styles.attachmentItem} key={file.id}>
                <span className={styles.attachmentName}>
                  {file.originalFileName}
                </span>
                <span className={styles.buttonGroup}>
                  <KibButtonNew
                    iconOnly
                    icon={<KibVisibilityOnIcon />}
                    size="small"
                    theme="utility"
                    onClick={() => handlePreview(file)}
                  />
                  <KibButtonNew
                    iconOnly
                    icon={<KibDeleteFillIcon />}
                    size="small"
                    theme="utility"
                    onClick={() => handleDeleteFile(file)}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LabTestItem
